
/// <amd-module name="Build/ArtifactsView/Components/PublishedArtifactsTabService" />
import * as React from "react";
import * as Resources from "Build/ArtifactsView/Resources"
import { ITabService } from "VSS/Features/PlatformUI/ContributedTab";
import { Services, VssService, IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Observer } from "VSSUI/Observer";
import { IVssContributedTab } from "VSSUI/Tabs.Types";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { ObservableArray, ObservableValue, IReadonlyObservableArray, IReadonlyObservableValue } from "VSS/Core/Observable";
import { PublishedArtifactsView } from "Build/ArtifactsView/Components/PublishedArtifactsView"
import { ArtifactsTreeData, ArtifactsDownloadRequestData, ArtifactsDownloadData } from "Build/Client/Contracts/DataProvider";
import { IPublishedArtifactsService } from "Build/ArtifactsView/PublishedArtifactsService.types";
import { ISignalRService } from "Build/Run/SignalRService";

const c_batchingDelayInMs = 3 * 1000;
const c_artifactsTreeProviderId = "ms.vss-build-web.run-artifacts-data-provider";
const artifactDirectoryType = "directory";

class PublishedArtifactsTabService extends VssService implements IPublishedArtifactsService, ITabService {
    public getTabs(tabId: string, contextualData? : any): IVssContributedTab[] | IReadonlyObservableArray<IVssContributedTab> {
        this._initialize();

        if (this._data.value && this._data.value.items.length > 0) {
            if (this._tabs.length === 0) {
                this._tabs.push(this._tab);
            }
        }

        return this._tabs;
    }

    public get data(): IReadonlyObservableValue<ArtifactsTreeData | undefined> {
        return this._data;
    }

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._initialize();
    }

    public _serviceRestart(pageContext: IVssPageContext): void {
        super._serviceRestart(pageContext);
        this._initialize();
    }

    public _serviceEnd(pageContext: IVssPageContext): any {
        super._serviceEnd(pageContext);
        this._timerManagement.clearAllTimers();
    }

    public refreshData = (): void => {
        this._refreshDataDebounced();
    }

    public startListening() {
        if (this._data.value) {
            this._signalR = this.pageContext.getService<ISignalRService>("ISignalRService");
            this._signalR.watch(this._data.value.buildId);
        }
    }

    public stopListening() {
        if (this._signalR) {
            if (this._data.value) {
                this._signalR.endWatch(this._data.value.buildId);
            }
            this._signalR = undefined;
        }
    }

    public getArtifactTree(artifactId: number): Promise<ArtifactsTreeData | undefined> {
        if (!this._data.value) {
            return Promise.resolve(undefined);
        }

        return this._contributionService().getDataAsync<ArtifactsTreeData>(c_artifactsTreeProviderId, { "artifactId": artifactId, "buildId": this._data.value.buildId }, false);
    }

    private _initialize() {
        if (!this._data.value) {
            const data = this._contributionService().getData<ArtifactsTreeData>(c_artifactsTreeProviderId);
            if (data) {
                this._data.value = data;
            }
    
            // Debouncing this data for SignalR
            this._timerManagement = new TimerManagement();
            this._refreshDataDebounced = this._timerManagement.debounce(this._refreshData, c_batchingDelayInMs);
        }
    }

    private _refreshData = async (): Promise<void> => {
        if (!this._data.value) {
            return Promise.resolve();
        }

        const data = await this._contributionService().getDataAsync<ArtifactsTreeData>(c_artifactsTreeProviderId, { "buildId": this._data.value.buildId }, true);
        if (data) {
            this._data.value = data;
        }
    }

    public getDownloadUrl = (artifactId: number, compressDownload: boolean, path?: string, artifactType?: string): Promise<string> => {
        if (!this._data.value) {
            return Promise.resolve("");
        }

        const isDirectoryType:Boolean = (artifactType === artifactDirectoryType);

        const params: ArtifactsDownloadRequestData = {
            artifactId: artifactId,
            buildId: this._data.value.buildId,
            compressDownload,
            path: path || "",
            saveAbsolutePath: !isDirectoryType
        }

        return this._contributionService().getDataAsync<ArtifactsDownloadData, ArtifactsDownloadRequestData>("ms.vss-build-web.run-artifacts-download-data-provider", params)
            .then((data) => {
                if (data) {
                    return data.downloadUrl;
                }
                return "";
            });
    }

    private _renderPublishedArtifacts = (): JSX.Element => {
        return (
            <Observer data = {this._data}>
                {
                    () => (
                        <PublishedArtifactsView service={this}/>
                    )
                }
            </Observer>
        );
    }

    private readonly _tab: IVssContributedTab = {
        id: "publishedArtifacts",
        name: Resources.PublishedArtifactsTabName,
        order: 1,
        render: this._renderPublishedArtifacts
    };

    private _contributionService = (): IVssContributionService => {
        return this.pageContext.getService<IVssContributionService>("IVssContributionService");
    }

    private _data: ObservableValue<ArtifactsTreeData | undefined> = new ObservableValue(undefined);
    private _tabs = new ObservableArray<IVssContributedTab>();
    private _signalR: ISignalRService | undefined;
    private _timerManagement: TimerManagement;
    private _refreshDataDebounced: () => void;
}

Services.add("pipelines-run-published-artifacts-tab-provider-service", { serviceFactory: PublishedArtifactsTabService });